import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout, { DateInput } from './Layout';
import { useNavigate } from 'react-router-dom';
import { formatDate, getApiUrl } from './config';

function PurchaseReport() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [purchases, setPurchases] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [suppliersById, setSuppliersById] = useState({});
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [viewInvoice, setViewInvoice] = useState(null);
    const navigate = useNavigate();

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Supplier WhatsApp number not found");
            return;
        }
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const formatPaymentSplit = (cash, upi, bank) => {
        const c = Number(cash) || 0;
        const u = Number(upi) || 0;
        const b = Number(bank) || 0;
        const parts = [];
        if (c > 0) parts.push(`Cash ₹ ${c.toFixed(2)}`);
        if (u > 0) parts.push(`UPI ₹ ${u.toFixed(2)}`);
        if (b > 0) parts.push(`Bank ₹ ${b.toFixed(2)}`);
        return parts.length > 0 ? parts.join(" | ") : "-";
    };

    const buildWhatsAppMessage = (row) => {
        const dateText = formatDate(row.date);
        const totalText = `₹ ${(row.total_amount || 0).toFixed(2)}`;
        const paidText = `₹ ${(row.paid_amount || 0).toFixed(2)}`;
        const balanceText = `₹ ${(row.invoice_balance || 0).toFixed(2)}`;
        return [
            `Purchase Invoice #${row.invoice_id}`,
            `Date: ${dateText}`,
            `Supplier: ${row.supplier_name || "-"}`,
            `Vehicle: ${row.vehicle_no || "-"}`,
            `Items: ${row.items_summary || "-"}`,
            `Total: ${totalText}`,
            `Paid: ${paidText}`,
            `Balance: ${balanceText}`
        ].join("\n");
    };

    const getSupplierPhone = (row) => {
        const fromReport = row.supplier_contact;
        if (fromReport !== undefined && fromReport !== null && String(fromReport).trim() !== "") return fromReport;
        return suppliersById[row.supplier_id] || "";
    };

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/vehicles`);
            setVehicles(res.data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchSuppliers = useCallback(async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/suppliers`);
            const map = {};
            (res.data || []).forEach(s => {
                map[s.id] = s.contact;
            });
            setSuppliersById(map);
            setSuppliers(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchPurchases = useCallback(async () => {
        try {
            const params = {};
            if (selectedVehicleId) params.vehicle_id = selectedVehicleId;
            if (selectedSupplierId) params.supplier_id = selectedSupplierId;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/reports/purchases`, {
                params
            });
            setPurchases(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [selectedVehicleId, selectedSupplierId, fromDate, toDate]);

    const handleExportTally = async () => {
        try {
            const params = { type: "purchases" };
            if (selectedVehicleId) params.vehicle_id = selectedVehicleId;
            if (selectedSupplierId) params.supplier_id = selectedSupplierId;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/exports/tally`, {
                params,
                responseType: "blob"
            });
            const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
            const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "tally_purchases.xml";
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(err);
            alert("Failed to export Tally XML");
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    useEffect(() => {
        fetchPurchases();
    }, [fetchPurchases]);

    const handleDelete = async (id) => {
        if (!isAdmin) return alert("Only admin can delete records");
        if (window.confirm("Are you sure you want to delete this invoice? Stock will be reversed.")) {
            try {
                await axios.delete(`${getApiUrl()}/purchases/${id}`);
                fetchPurchases();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleView = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/purchases/${id}`);
            setViewInvoice(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleExportPdf = () => {
        window.print();
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Purchase Report</h2>
                <div className="row g-3 align-items-end mb-3 no-print">
                    <div className="col-md-3">
                        <label className="form-label fw-bold">From</label>
                        <DateInput value={fromDate} onChange={setFromDate} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">To</label>
                        <DateInput value={toDate} onChange={setToDate} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Vehicle No</label>
                        <select className="form-select" value={selectedVehicleId} onChange={e => setSelectedVehicleId(e.target.value)}>
                            <option value="">All Vehicles</option>
                            {vehicles.map(v => (
                                <option key={v.id} value={v.id}>{v.vehicle_no}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Supplier</label>
                        <select className="form-select" value={selectedSupplierId} onChange={e => setSelectedSupplierId(e.target.value)}>
                            <option value="">All Suppliers</option>
                            {suppliers.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-primary w-100" onClick={handleExportTally}>
                            Export Tally XML
                        </button>
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-outline-dark w-100" onClick={handleExportPdf}>
                            Export PDF
                        </button>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Supplier</th>
                                <th>Vehicle No</th>
                                <th>Items</th>
                                <th>Qty</th>
                                <th>Weight</th>
                                <th>Rate</th>
                                <th>Invoice Total</th>
                                <th>Paid Amount</th>
                                <th>Paid Split</th>
                                <th>Voucher</th>
                                <th>Voucher Split</th>
                                <th>Net Bal</th>
                                <th className="no-print">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchases.map(p => (
                                <tr key={p.invoice_id}>
                                    <td>{formatDate(p.date)}</td>
                                    <td>{p.supplier_name}</td>
                                    <td>{p.vehicle_no || '-'}</td>
                                    <td>{p.items_summary}</td>
                                    <td>{Number(p.total_qty || 0)}</td>
                                    <td>{Number(p.total_weight || 0).toFixed(2)}</td>
                                    <td>{Number(p.rate_per_kg || 0).toFixed(2)}</td>
                                    <td className="fw-bold">₹ {(p.total_amount || 0).toFixed(2)}</td>
                                    <td className="text-success">₹ {(p.paid_amount || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(p.cash_amount, p.upi_amount, p.bank_amount)}</td>
                                    <td className="text-success">₹ {(Number(p.voucher_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(p.voucher_cash_amount, p.voucher_upi_amount, p.voucher_bank_amount)}</td>
                                    <td className="text-danger fw-bold">₹ {(Number(p.cumulative_balance ?? p.net_balance) || 0).toFixed(2)}</td>
                                    <td className="no-print">
                                        <button className="btn btn-info btn-sm me-1" onClick={() => handleView(p.invoice_id)}>View</button>
                                        <button
                                            className="btn btn-success btn-sm me-1"
                                            onClick={() => openWhatsApp(getSupplierPhone(p), buildWhatsAppMessage(p))}
                                        >
                                            WhatsApp
                                        </button>
                                        {isAdmin && (
                                            <>
                                                <button type="button" className="btn btn-warning btn-sm me-1" onClick={() => navigate(`/purchase?edit=${p.invoice_id}`)}>Edit</button>
                                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(p.invoice_id)}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* View Modal */}
                {viewInvoice && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Invoice #{viewInvoice.id}</h5>
                                    <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Date:</strong> {formatDate(viewInvoice.date)}</p>
                                    <p><strong>Supplier ID:</strong> {viewInvoice.supplier_id}</p>
                                    <p><strong>Vehicle:</strong> {viewInvoice.vehicle_no || '-'}</p>
                                    <p><strong>Paid Amount:</strong> ₹ {(Number(viewInvoice.paid_amount) || 0).toFixed(2)} ({formatPaymentSplit(viewInvoice.cash_amount, viewInvoice.upi_amount, viewInvoice.bank_amount)})</p>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Quantity</th>
                                                <th>Price</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewInvoice.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.product_name}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.price}</td>
                                                    <td>{item.total}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan="3" className="text-end">Grand Total</th>
                                                <th>₹ {(viewInvoice.total_amount || 0).toFixed(2)}</th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setViewInvoice(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default PurchaseReport;
